import React, { useState, useEffect, useCallback } from "react";
import {
  Modal,
  Paper,
  Button,
  Typography,
  CircularProgress,
} from "@material-ui/core";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ErrorIcon from "@material-ui/icons/Error";
import {
  sendEmergencySms,
  getCurrentPosition,
} from "../../../services/emergencyContactService";
import { useEmergencyAlertStyles } from "./EmergencyAlertStyles";

interface EmergencyContactModalProps {
  open: boolean;
  onClose: () => void;
  usuario: {
    cuil?: string;
    contactoEmergencia?: string;
    contactoEmergenciaNombre?: string;
    contactoEmergenciaParentesco?: string;
  };
}

const COUNTDOWN_KEY = "emergency_contact_countdown";
const COUNTDOWN_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

export const EmergencyAlert: React.FC<EmergencyContactModalProps> = ({
  open,
  onClose,
  usuario,
}) => {
  const classes = useEmergencyAlertStyles();
  const [isLoading, setIsLoading] = useState(false);

  const [result, setResult] = useState<{
    success: boolean;
    message: string;
    minutosRestantes?: number;
  } | null>(null);
  const [remainingTime, setRemainingTime] = useState(0);

  const checkCountdown = useCallback(() => {
    const countdownEnd = localStorage.getItem(COUNTDOWN_KEY);
    if (countdownEnd) {
      const now = Date.now();
      const endTime = parseInt(countdownEnd, 10);
      if (now < endTime) {
        setRemainingTime(Math.ceil((endTime - now) / 1000 / 60)); // minutes
        return true;
      } else {
        localStorage.removeItem(COUNTDOWN_KEY);
        setRemainingTime(0);
      }
    }
    return false;
  }, []);

  const handleSendAlert = useCallback(async () => {
    if (!usuario?.cuil || !usuario?.contactoEmergencia) {
      setResult({
        success: false,
        message: "Faltan datos de contacto de emergencia",
      });
      setIsLoading(false);
      return;
    }

    try {
      const position = await getCurrentPosition();
      const response = await sendEmergencySms(
        usuario.cuil,
        position.latitude,
        position.longitude,
        usuario.contactoEmergencia
      );
      if (response.ok && response.return.enviado) {
        const countdownEnd = Date.now() + COUNTDOWN_DURATION;
        localStorage.setItem(COUNTDOWN_KEY, countdownEnd.toString());
        setRemainingTime(5);
        setResult({
          success: true,
          message: "Alerta enviada exitosamente",
        });
      } else {
        setResult({
          success: false,
          message: response.error || "Error al enviar la alerta",
          minutosRestantes: response.return?.minutosRestantes,
        });
      }
    } catch (error) {
      console.error("Error sending emergency SMS:", error);
      setResult({
        success: false,
        message: "Error al enviar la alerta",
      });
    } finally {
      setIsLoading(false);
    }
  }, [usuario]);

  useEffect(() => {
    if (open) {
      setIsLoading(true);
      setResult(null);
      const hasCountdown = checkCountdown();

      if (hasCountdown) {
        setResult({
          success: true,
          message: "Tu alerta ya fue enviada",
          minutosRestantes: remainingTime,
        });
        setIsLoading(false);
      } else {
        handleSendAlert();
      }
    }
  }, [open, remainingTime, checkCountdown, handleSendAlert]);

  const handleClose = () => {
    if (isLoading) return;

    setResult(null);
    setIsLoading(false);
    onClose();
  };

  const getTitle = () => {
    if (isLoading) return "Enviando alerta...";
    if (result?.success) return "Alerta enviada";
    if (result && !result.success) return "Error al enviar";
    return "Enviando alerta...";
  };

  const getMessage = () => {
    if (result?.success) {
      if (result.minutosRestantes) {
        return `Tu aviso fue enviado al contacto de emergencia que elegiste. Podrás enviar una nueva alerta en ${result.minutosRestantes} minutos.`;
      }
      return "Tu aviso fue enviado al contacto de emergencia que elegiste. Ya está al tanto de la situación y pronto se pondrá en contacto contigo.";
    }
    if (result && !result.success) {
      return result.message;
    }
    return "Procesando...";
  };

  const showIcon = () => {
    if (isLoading) {
      return <CircularProgress size={48} className={classes.spinner} />;
    }
    if (result?.success) {
      return <CheckCircleIcon className={classes.successIcon} />;
    }
    if (result && !result.success) {
      return <ErrorIcon className={classes.errorIcon} />;
    }
    return null;
  };

  return (
    <Modal
      open={open}
      onClose={() => {
        if (isLoading) {
          return;
        }
        handleClose();
      }}
      className={classes.modal}
    >
      <Paper className={classes.paper} onClick={(e) => e.stopPropagation()}>
        <div className={classes.dialogTitle}>
          {showIcon()}
          <Typography variant="h6">{getTitle()}</Typography>
        </div>

        <div className={classes.dialogContent}>
          {!isLoading && (
            <Typography className={classes.message}>{getMessage()}</Typography>
          )}
        </div>

        {!isLoading && (
          <div className={classes.dialogActions}>
            <Button
              onClick={handleClose}
              className={classes.button}
              color="primary"
              variant="contained"
            >
              Aceptar
            </Button>
          </div>
        )}
      </Paper>
    </Modal>
  );
};
